import { Routes, ɵEmptyOutletComponent } from '@angular/router';
import { languages } from './consts';
import { externalRedirectGuard } from './guards/external-redirect.guard';
import { growthBookGuard } from './guards/growth-book.guard';
import { loggedInRedirectGuard } from './guards/logged-in-redirect.guard';
import { notFoundGuard } from './guards/not-found.guard';
import { promoSaveGuard } from './guards/promo-save.guard';
import { trackingIdGuard } from './guards/tracking-id.guard';
import { websiteMetaResolver } from './resolves/website-meta.resolver';
import { defaultLocaleGuard } from './storyblok/guards/default-locale.guard';
import { isNonDefaultLocaleGuard } from './storyblok/guards/is-non-default-locale.guard';
import { routes as storyblokRoutes } from './storyblok/routes';
import { prefetchWebsiteChromeGuard } from './storyblok/website-chrome.service';

const redirects: Routes = [
  {
    path: 'login',
    redirectTo: '/auth/login',
    pathMatch: 'full',
  },
  {
    path: 'signup',
    redirectTo: '/auth/signup',
    pathMatch: 'full',
  },
  {
    path: 'password-reset',
    redirectTo: '/auth/password-reset',
    pathMatch: 'full',
  },
  {
    path: 'account',
    redirectTo: '/a',
    pathMatch: 'full',
  },
  {
    path: 'activity',
    redirectTo: '/a/activity',
    pathMatch: 'full',
  },
  {
    path: 'support',
    data: {
      redirectTo: 'https://help.picflow.com/en',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'legal',
    data: {
      redirectTo: 'https://help.picflow.com/en/collections/3116271-legal',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'mea',
    data: {
      redirectTo: 'https://docs.google.com/document/d/1JZ59piX6s5at3N2PpICLUBoN8BWkf3eQzS3Vqi_NVB4',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'dpa',
    data: {
      redirectTo: 'https://help.picflow.com/en/articles/5538130-data-processing-addendum',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'feedback',
    data: {
      redirectTo: 'https://feedback.picflow.com',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'cookies',
    data: {
      redirectTo: 'https://help.picflow.com/en/articles/5538125-cookie-policy',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'privacy',
    data: {
      redirectTo: 'https://help.picflow.com/en/articles/5538118-privacy-policy',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
  {
    path: 'terms',
    data: {
      redirectTo: 'https://help.picflow.com/en/articles/5538133-terms-of-service',
    },
    canActivate: [externalRedirectGuard],
    component: ɵEmptyOutletComponent,
  },
];

const allRoutes: Routes = [
  {
    path: 'convert/:format',
    loadChildren: () => import('./tools/convert/routes').then(m => m.routes),
    canActivate: [prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: ':lang/convert/:format',
    loadChildren: () => import('./tools/convert/routes').then(m => m.routes),
    canMatch: [isNonDefaultLocaleGuard(languages.filter(l => l.i18n.website).map(l => l.lang))],
    canActivate: [defaultLocaleGuard, prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'compare',
    loadChildren: () => import('./compare/routes').then(m => m.routes),
    canActivate: [prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: ':lang/compare',
    loadChildren: () => import('./compare/routes').then(m => m.routes),
    canMatch: [isNonDefaultLocaleGuard(languages.filter(l => l.i18n.website).map(l => l.lang))],
    canActivate: [defaultLocaleGuard, prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'updates/:updateSlug',
    loadChildren: () => import('./updates/routes').then(m => m.routes),
    canActivate: [prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: ':lang/updates/:updateSlug',
    loadChildren: () => import('./updates/routes').then(m => m.routes),
    canActivate: [prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'a',
    loadChildren: () => import('./account/routes').then(m => m.routes),
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'r',
    loadChildren: () => import('./referral/routes').then(m => m.routes),
    canActivate: [prefetchWebsiteChromeGuard],
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/routes').then(m => m.routes),
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'cant-edit-gallery',
    loadChildren: () => import('./public-cant-edit-gallery/routes').then(m => m.routes),
  },
  {
    path: 'maintenance',
    loadChildren: () => import('./maintenance/routes').then(m => m.routes),
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: 'iframe-auth',
    loadChildren: () => import('./iframe-auth/routes').then(m => m.routes),
  },
  {
    path: 'technical',
    loadChildren: () => import('./technical/routes').then(m => m.routes),
  },
  { path: 'sso-callback', loadChildren: () => import('./auth/sso-callback-routes').then(m => m.routes) },
  ...redirects,
  {
    path: '',
    children: storyblokRoutes,
    resolve: {
      meta: websiteMetaResolver,
    },
  },
  {
    path: '**',
    canActivate: [notFoundGuard],
    loadComponent: () => import('./pages/not-found-page/not-found-page.component').then(m => m.NotFoundPageComponent),
    resolve: {
      meta: websiteMetaResolver,
    },
  },
];

export const routes: Routes = [
  {
    path: '',
    canActivate: [loggedInRedirectGuard, trackingIdGuard, promoSaveGuard, growthBookGuard],
    children: allRoutes,
  },
];
